import { useEffect, useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";

import { api } from "../api/client";
import { mensajeDeError } from "../api/mensajes";
import type { SigningSecret } from "../api/types";
import { failureMessage, useResource } from "../api/useResource";
import { useSession } from "../store/session";
import { DataTable } from "../components/data-table";
import {
  ActionError, Badge, Button, Card, Field, PageHeader, inputClass,
} from "../components/ui";
import { formatDateTime } from "./Calls";

// Seguridad: el segundo factor del usuario y los secretos de firma de la
// cuenta. Son dos cosas distintas y la pantalla las separa: el TOTP es de la
// persona, el secreto de firma es de la cuenta y lo necesita el servidor del
// cliente para validar lo que le llega.
//
// # Por qué los códigos de recuperación se muestran una sola vez
//
// Porque se guardan hasheados, con el mismo algoritmo que las contraseñas.
// Recuperarlos no se puede, y no se debe poder: si el servidor pudiera
// mostrarlos otra vez, un atacante con acceso a la base también podría. Si el
// usuario los pierde, se regeneran.
export function Security() {
  const session = useSession((s) => s.session);
  const reload = useSession((s) => s.load);
  const can = useSession((s) => s.can);
  const mfaRequerido = useSession((s) => s.mfaRequerido);
  // El alta ya terminó en el servidor, pero la sesión no se recarga hasta que
  // la persona descarta los códigos de recuperación. Sin esto, la insignia
  // diría «Inactiva» justo al lado de «ya está activo».
  const [recienActivado, setRecienActivado] = useState(false);

  const enabled = session?.user.mfa_enabled ?? false;
  const activo = enabled || recienActivado;

  return (
    <div>
      <PageHeader
        title="Seguridad"
        description="El segundo factor es de tu usuario, no de la cuenta. Los secretos de firma sí son de la cuenta: con ellos tu servidor comprueba que lo que recibe viene de la plataforma."
      />

      {/*
        Sin este aviso, el rebote es mudo: la API devuelve 403 en todas las
        pantallas de negocio, el portal trae aquí, y la persona ve una pantalla
        de seguridad cualquiera sin saber por qué no puede hacer nada. Eso es
        exactamente el diagnóstico imposible que hay que evitar.
      */}
      {mfaRequerido && !enabled && (
        <Card className="mb-6 border-amber-500/40 bg-amber-500/[0.08] p-4">
          <p className="text-sm font-semibold text-amber-200">
            Tu rol exige un segundo factor. Hasta configurarlo, el portal no puede hacer nada más.
          </p>
          <p className="mt-1.5 text-sm text-amber-100/80">
            Por eso te trajimos aquí: crear troncales, cuentas, números o llaves devuelve «prohibido»
            mientras falte. Se configura una vez, abajo, con cualquier app de autenticación
            (Google Authenticator, Authy, 1Password). Toma menos de un minuto.
          </p>
        </Card>
      )}

      <CambiarContrasena tieneMFA={activo} />

      <Card className="mb-6 p-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-sm font-medium text-slate-100">
            Autenticación en dos pasos (TOTP)
          </h2>
          {activo ? <Badge tone="good">Activa</Badge> : <Badge tone="warn">Inactiva</Badge>}
        </div>

        {enabled ? (
          <Enabled onChanged={() => void reload()} />
        ) : (
          <Enrollment
            onConfirmed={() => setRecienActivado(true)}
            onEnabled={() => void reload()}
          />
        )}
      </Card>

      {can("signing_secrets.manage") && <SigningSecrets />}
    </div>
  );
}

/**
 * CambiarContrasena cambia la contraseña del usuario de la sesión.
 *
 * Pide probar la identidad otra vez, y no solo tener la sesión abierta: el
 * portal se usa en equipos compartidos, y sin esa prueba una pantalla sin
 * bloquear es una cuenta regalada.
 *
 * Quien tiene segundo factor puede probarla con un código en lugar de la
 * contraseña actual. No es un atajo cómodo: es la única salida de quien no la
 * recuerda y no tiene a nadie por encima que se la restablezca —el
 * superadministrador de plataforma, por ejemplo, que no pertenece a ninguna
 * cuenta y por tanto no tiene administrador que le ayude.
 */
function CambiarContrasena({ tieneMFA }: { tieneMFA: boolean }) {
  const [actual, setActual] = useState("");
  const [codigo, setCodigo] = useState("");
  const [nueva, setNueva] = useState("");
  const [repetida, setRepetida] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [listo, setListo] = useState(false);
  const [busy, setBusy] = useState(false);

  // Mínimo del backend (`secrets.MinPasswordLength`). Se repite aquí para
  // avisar antes de enviar, no para autorizar: quien manda es el servidor.
  const MINIMO = 12;

  const sinPrueba = actual.trim() === "" && codigo.trim() === "";
  const noCoinciden = repetida !== "" && nueva !== repetida;
  const corta = nueva !== "" && [...nueva].length < MINIMO;

  async function enviar(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.post("/auth/password", {
        ...(actual.trim() === "" ? {} : { current_password: actual }),
        ...(codigo.trim() === "" ? {} : { totp_code: codigo.trim() }),
        new_password: nueva,
      });
      setListo(true);
      setActual("");
      setCodigo("");
      setNueva("");
      setRepetida("");
    } catch (err) {
      setError(mensajeDeError(err, "No se pudo cambiar la contraseña."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="mb-6 p-4">
      <h2 className="text-sm font-medium text-slate-100">Contraseña</h2>
      <p className="mt-1 text-sm text-slate-400">
        Al cambiarla se cierran tus otras sesiones —el móvil, el otro navegador—; esta se queda
        dentro. {tieneMFA && "Si no recuerdas la actual, prueba tu identidad con un código."}
      </p>

      {listo && (
        <Card className="mt-4 border-emerald-500/30 bg-emerald-500/10 p-3">
          <p className="text-sm text-emerald-200">
            Contraseña cambiada. Las demás sesiones quedaron cerradas.
          </p>
        </Card>
      )}

      <form onSubmit={(e) => void enviar(e)} className="mt-4 grid gap-4 sm:max-w-md">
        <Field label="Contraseña actual" hint={tieneMFA ? "O un código, abajo." : undefined}>
          <input
            type="password"
            autoComplete="current-password"
            className={inputClass}
            value={actual}
            onChange={(e) => setActual(e.target.value)}
          />
        </Field>

        {tieneMFA && (
          <Field
            label="Código del segundo factor"
            hint="Los seis dígitos de tu app, o uno de recuperación. Sustituye a la contraseña actual."
          >
            <input
              type="text"
              autoComplete="one-time-code"
              className={inputClass}
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
            />
          </Field>
        )}

        <Field
          label="Contraseña nueva"
          hint={`Mínimo ${MINIMO} caracteres.`}
          error={corta ? `Faltan caracteres: el mínimo es ${MINIMO}.` : undefined}
        >
          <input
            type="password"
            autoComplete="new-password"
            className={inputClass}
            value={nueva}
            onChange={(e) => setNueva(e.target.value)}
          />
        </Field>

        <Field
          label="Repite la nueva"
          error={noCoinciden ? "No coinciden." : undefined}
        >
          <input
            type="password"
            autoComplete="new-password"
            className={inputClass}
            value={repetida}
            onChange={(e) => setRepetida(e.target.value)}
          />
        </Field>

        {error !== null && <ActionError message={error} />}

        <div className="flex items-center gap-3">
          <Button
            type="submit"
            disabled={busy || sinPrueba || corta || nueva === "" || nueva !== repetida}
          >
            {busy ? "Cambiando…" : "Cambiar contraseña"}
          </Button>
          {sinPrueba && (
            <span className="text-xs text-slate-500">
              {tieneMFA
                ? "Escribe la contraseña actual o un código."
                : "Escribe tu contraseña actual."}
            </span>
          )}
        </div>
      </form>
    </Card>
  );
}

function Enrollment({
  onConfirmed,
  onEnabled,
}: {
  /** El servidor ya activó el factor: sirve para la insignia. */
  onConfirmed: () => void;
  /** La persona ya guardó los códigos: ahora sí se recarga la sesión. */
  onEnabled: () => void;
}) {
  const [secret, setSecret] = useState<string | null>(null);
  const [uri, setURI] = useState("");
  const [qr, setQR] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copiado, setCopiado] = useState(false);

  // El QR se dibuja **aquí**, no se pide a ningún servicio.
  //
  // Mandar un `otpauth://` a un generador de QR de internet es entregarle el
  // secreto del segundo factor a un tercero; y además la CSP del portal lo
  // bloquearía (`img-src 'self' data:`). La librería va en el bundle y produce
  // un PNG en base64 que cumple esa política.
  useEffect(() => {
    if (uri === "") return;
    let vivo = true;
    // Carga bajo demanda: el generador llega solo cuando alguien da de alta el
    // segundo factor, no en cada visita al portal.
    void import("qrcode")
      .then(({ default: QRCode }) =>
        QRCode.toDataURL(uri, {
          margin: 2,
          width: 220,
          // Oscuro sobre blanco: un QR claro sobre fondo oscuro falla en
          // muchos lectores, y aquí lo que importa es que la cámara lo lea a
          // la primera.
          color: { dark: "#05070f", light: "#ffffff" },
        }),
      )
      .then((url) => vivo && setQR(url))
      .catch(() => vivo && setQR(null));
    return () => {
      vivo = false;
    };
  }, [uri]);

  async function start() {
    setError(null);
    setBusy(true);
    try {
      const data = await api.post<{ secret: string; uri: string }>("/auth/mfa/setup");
      setSecret(data.secret);
      setURI(data.uri);
    } catch (err) {
      setError(mensajeDeError(err, "No se pudo empezar el alta."));
    } finally {
      setBusy(false);
    }
  }

  async function confirm(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const data = await api.post<{ recovery_codes: string[] }>("/auth/mfa/confirm", {
        code: code.trim(),
      });
      // Ojo con el orden: **no** se avisa todavía. Avisar aquí recarga la
      // sesión, `mfa_enabled` pasa a true y esta pantalla se reemplaza por la
      // de "ya está activo", llevándose los códigos de recuperación antes de
      // que nadie los haya anotado. Son la única forma de entrar si se pierde
      // el teléfono, así que el aviso espera a que la persona los descarte.
      setCodes(data.recovery_codes);
      onConfirmed();
    } catch (err) {
      setError(mensajeDeError(err, "El código no se pudo confirmar."));
    } finally {
      setBusy(false);
    }
  }

  async function copiarSecreto() {
    if (secret === null) return;
    try {
      await navigator.clipboard.writeText(secret);
      setCopiado(true);
    } catch {
      // Sin contexto seguro o sin permiso, el portapapeles falla. Decirlo es
      // mejor que un botón que aparenta haber copiado.
      setCopiado(false);
      setError("El navegador no permitió copiar: selecciona el secreto a mano.");
    }
  }

  if (codes) {
    return <RecoveryCodes codes={codes} onDone={onEnabled} />;
  }

  if (secret === null) {
    return (
      <div className="mt-3">
        <p className="text-sm text-slate-400">
          Al activarla, cada inicio de sesión pedirá un código de tu aplicación de
          autenticación además de la contraseña.
        </p>
        <Button className="mt-3" disabled={busy} onClick={() => void start()}>
          {busy ? "Preparando…" : "Activar"}
        </Button>
        {error && <p className="mt-2 text-xs text-rose-300">{error}</p>}
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-5">
      <ol className="space-y-5">
        <li className="flex gap-3">
          <Paso n={1} />
          <div className="min-w-0 flex-1">
            <p className="text-sm text-slate-200">
              Escanea este código con tu aplicación de autenticación.
            </p>
            <p className="mt-1 text-xs text-slate-500">
              Google Authenticator, Authy, 1Password, Microsoft Authenticator: cualquiera sirve.
            </p>
            <div className="mt-3 flex flex-wrap items-start gap-4">
              {qr !== null ? (
                <img
                  src={qr}
                  alt="Código QR para la aplicación de autenticación"
                  className="rounded-lg bg-white p-2"
                  width={220}
                  height={220}
                />
              ) : (
                <div className="flex h-[220px] w-[220px] items-center justify-center rounded-lg border border-white/10 bg-white/[0.04] text-xs text-slate-500">
                  Preparando el código…
                </div>
              )}

              <div className="min-w-0 flex-1 space-y-3">
                {/* Desde el teléfono no hay cámara que apunte a su propia
                    pantalla: el enlace abre la aplicación directamente. */}
                <a
                  href={uri}
                  className="inline-block rounded-lg border border-white/15 bg-white/[0.04] px-3 py-2 text-sm text-sky-300 transition hover:bg-white/[0.09] hover:text-sky-200"
                >
                  ¿Estás en el teléfono? Abrir la app directamente
                </a>

                <div>
                  <p className="text-xs text-slate-500">O escribe este secreto a mano:</p>
                  <p className="mt-1 break-all font-mono text-sm tracking-wider text-slate-100">
                    {secret.replace(/(.{4})/g, "$1 ").trim()}
                  </p>
                  <Button variant="secondary" className="mt-2" onClick={() => void copiarSecreto()}>
                    {copiado ? "Copiado" : "Copiar secreto"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </li>

        <li className="flex gap-3">
          <Paso n={2} />
          <div className="min-w-0 flex-1">
            <p className="text-sm text-slate-200">
              Escribe el código de seis dígitos que te muestre la aplicación.
            </p>
            <p className="mt-1 text-xs text-slate-500">
              Hasta que lo confirmes, el segundo factor <strong>no está activo</strong>: así nadie
              se queda fuera de su cuenta por perder el código a mitad del alta.
            </p>
            <form className="mt-3 flex flex-wrap items-end gap-2" onSubmit={(e) => void confirm(e)}>
              <Field label="Código de seis dígitos">
                <input
                  className={`${inputClass} max-w-[10rem] font-mono text-lg tracking-[0.3em]`}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                />
              </Field>
              <Button type="submit" disabled={busy || code.trim().length !== 6}>
                {busy ? "Confirmando…" : "Confirmar y activar"}
              </Button>
            </form>
          </div>
        </li>
      </ol>

      {error && <ActionError message={error} />}
    </div>
  );
}

/** El número del paso, para que la secuencia se lea de un vistazo. */
function Paso({ n }: { n: number }) {
  return (
    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-sky-400/40 bg-sky-500/10 font-mono text-xs text-sky-300">
      {n}
    </span>
  );
}

function RecoveryCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const [copiado, setCopiado] = useState<"idle" | "ok" | "fallo">("idle");

  async function copiar() {
    try {
      await navigator.clipboard.writeText(codes.join("\n"));
      setCopiado("ok");
    } catch {
      setCopiado("fallo");
    }
  }

  return (
    <div className="mt-3">
      <div className="rounded-lg border border-amber-500/40 bg-amber-500/[0.08] p-4">
        <p className="text-sm font-semibold text-amber-200">
          El segundo factor ya está activo. Guarda estos códigos antes de continuar.
        </p>
        <p className="mt-1.5 text-sm text-amber-100/80">
          No se vuelven a mostrar. Cada uno sirve <strong>una vez</strong> y es lo único que te
          deja entrar si pierdes el teléfono.
        </p>
        <ul className="mt-4 grid grid-cols-2 gap-x-6 gap-y-1.5 font-mono text-sm text-slate-100 sm:grid-cols-3">
          {codes.map((code) => (
            <li key={code}>{code}</li>
          ))}
        </ul>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button variant="secondary" onClick={() => void copiar()}>
            {copiado === "ok" ? "Copiados" : "Copiar todos"}
          </Button>
          {/* Nada se cierra solo: esta pantalla no se puede volver a abrir. */}
          <Button onClick={onDone}>Ya los guardé</Button>
          {copiado === "fallo" && (
            <span className="text-xs text-amber-200">
              El navegador no permitió copiar: anótalos a mano.
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function Enabled({ onChanged }: { onChanged: () => void }) {
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  async function disable(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.post("/auth/mfa/disable", { code: code.trim() });
      setCode("");
      setConfirming(false);
      onChanged();
    } catch (err) {
      setError(mensajeDeError(err, "No se pudo desactivar el segundo factor."));
    } finally {
      setBusy(false);
    }
  }

  async function regenerate() {
    setError(null);
    setBusy(true);
    try {
      const data = await api.post<{ recovery_codes: string[] }>("/auth/mfa/recovery-codes");
      setCodes(data.recovery_codes);
    } catch (err) {
      setError(mensajeDeError(err, "No se pudieron generar los códigos."));
    } finally {
      setBusy(false);
    }
  }

  if (codes) return <RecoveryCodes codes={codes} onDone={() => setCodes(null)} />;

  return (
    <div className="mt-3 space-y-4">
      <p className="text-sm text-slate-400">
        Cada inicio de sesión pide un código de tu aplicación. Un código de recuperación
        también sirve, y se gasta al usarlo.
      </p>

      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" disabled={busy} onClick={() => void regenerate()}>
          Generar códigos de recuperación nuevos
        </Button>
        {!confirming && (
          <Button variant="danger" onClick={() => setConfirming(true)}>
            Desactivar
          </Button>
        )}
      </div>

      {confirming && (
        <form
          className="flex items-end gap-2 rounded-md border border-rose-500/30 bg-rose-500/10 p-3"
          onSubmit={(e) => void disable(e)}
        >
          <Field
            label="Código actual o de recuperación"
            hint="se pide para que una sesión robada no pueda apagar la protección"
          >
            <input
              className={inputClass + " font-mono"}
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </Field>
          <Button type="submit" variant="danger" disabled={busy || code.trim() === ""}>
            {busy ? "Desactivando…" : "Desactivar"}
          </Button>
          <Button variant="secondary" onClick={() => setConfirming(false)}>
            Cancelar
          </Button>
        </form>
      )}

      <p className="text-xs text-slate-500">
        Generar códigos nuevos invalida los anteriores.
      </p>
      {error && <p className="text-xs text-rose-300">{error}</p>}
    </div>
  );
}

/**
 * SigningSecrets administra los secretos HMAC de la cuenta.
 *
 * # Por qué la rotación deja dos secretos vivos
 *
 * Porque el servidor del cliente no se actualiza en el mismo instante que la
 * plataforma. Durante la ventana, IAMEDIA firma con el nuevo y manda **las dos
 * firmas** en la cabecera (ADR-010); el receptor acepta si alguna cuadra. Sin
 * esa ventana, rotar significaría rechazar todas las peticiones hasta que el
 * cliente desplegara el secreto nuevo.
 *
 * El secreto en claro se muestra una sola vez, al rotar, por lo mismo que la
 * llave de API: la plataforma lo guarda cifrado y no hay pantalla que lo
 * recupere.
 */
function SigningSecrets() {
  const secrets = useResource<SigningSecret[]>("/api/v1/signing-secrets");

  const [fresh, setFresh] = useState<SigningSecret | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const retiringAt =
    secrets.data?.find((s) => s.status === "rotating")?.retires_at ?? null;

  async function rotate() {
    setError(null);
    setBusy(true);
    try {
      setFresh(await api.post<SigningSecret>("/api/v1/signing-secrets/rotate"));
      await secrets.reload(true);
    } catch (err) {
      setError(failureMessage(err, "no se pudo rotar el secreto"));
    } finally {
      setBusy(false);
    }
  }

  const columnas = useMemo<ColumnDef<SigningSecret, any>[]>(
    () => [
      {
        id: "status",
        accessorKey: "status",
        header: "Estado",
        meta: { label: "Estado", width: "9rem" },
        cell: ({ row }) => (
          <Badge tone={SECRET_TONES[row.original.status]}>
            {SECRET_LABELS[row.original.status]}
          </Badge>
        ),
      },
      {
        id: "activated_at",
        accessorKey: "activated_at",
        header: "Activo desde",
        meta: { label: "Activo desde" },
        cell: ({ row }) => (
          <span className="text-slate-600">{formatDateTime(row.original.activated_at)}</span>
        ),
      },
      {
        id: "retires_at",
        accessorKey: "retires_at",
        header: "Se retira",
        meta: { label: "Se retira" },
        cell: ({ row }) => (
          <span className="text-slate-600">
            {row.original.retires_at === null ? "—" : formatDateTime(row.original.retires_at)}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-medium text-slate-100">Secretos de firma</h2>
        <Button variant="secondary" disabled={busy} onClick={() => void rotate()}>
          {busy ? "Rotando…" : "Rotar secreto"}
        </Button>
      </div>
      <p className="mt-1.5 max-w-prose text-sm text-slate-400">
        Con estos secretos se firman las peticiones a tu Voice URL, los webhooks y el handshake de
        Media Streams. Al rotar, el anterior sigue siendo válido durante una ventana y la
        plataforma manda las dos firmas, para que no haya que desplegar los dos lados a la vez.
      </p>

      {error !== null && (
        <div className="mt-4">
          <ActionError message={error} />
        </div>
      )}

      {fresh?.secret !== undefined && (
        <Card className="mt-4 border-emerald-500/30 bg-emerald-500/10 p-4">
          <p className="text-sm font-semibold text-emerald-200">
            Secreto nuevo. No se vuelve a mostrar.
          </p>
          {/*
            La fecha de retiro vive en el secreto **anterior**, no en el nuevo:
            el que acaba de nacer no se retira, y el que se retira es el que ya
            estaba. Por eso se lee de la fila en rotación y no de la respuesta.
          */}
          <p className="mt-1 text-sm text-emerald-200">
            Cárgalo en tu servidor antes de que se retire el anterior
            {retiringAt !== null && <> ({formatDateTime(retiringAt)})</>}: a partir de ese momento
            las firmas viejas se dejan de enviar y tu servidor tiene que validar con el nuevo.
          </p>
          <code className="mt-3 block overflow-x-auto rounded border border-emerald-500/30 bg-emerald-500/[0.06] px-3 py-2 font-mono text-xs text-slate-100">
            {fresh.secret}
          </code>
          <div className="mt-3">
            <Button variant="secondary" onClick={() => setFresh(null)}>
              Ya lo guardé
            </Button>
          </div>
        </Card>
      )}

      <div className="mt-4">
        <DataTable<SigningSecret>
          data={secrets.data}
          columns={columnas}
          getRowId={(secret) => secret.id}
          loading={secrets.loading}
          error={secrets.error}
          onRetry={() => void secrets.reload(false)}
          caption="Secretos de firma"
          empty={{
            title: "Sin secretos de firma",
            description:
              "Sin un secreto activo, tu servidor no puede comprobar que lo que recibe viene de la plataforma.",
          }}
        />
      </div>
    </Card>
  );
}

const SECRET_LABELS: Record<SigningSecret["status"], string> = {
  active: "Activo",
  rotating: "En rotación",
  retired: "Retirado",
};

const SECRET_TONES: Record<SigningSecret["status"], "neutral" | "good" | "warn" | "bad"> = {
  active: "good",
  rotating: "warn",
  retired: "neutral",
};
